import {useState} from 'react'
import {FullLoader} from 'components/atoms/FullLoader/FullLoader'
import {Link} from 'react-router-dom'
import {Button} from 'shared/ui'
import {notifyError} from 'shared/errors'
import {api as webhooksApi} from '../../api'
import type {CreateWebhookPayload} from '../../types'
import {WebhookList} from './WebhookList'
import {WebhookForm} from './components/WebhookForm'
import {useWebhookForm} from './hooks/useWebhookForm'
import {useWebhookSubscription} from './hooks/useWebhookSubscription'
import {typo} from 'shared/styles'
import css from './WebhooksContent.module.css'

// Keep UI limit aligned with backend Webhooks:MaxWebhooksPerWorkspace config.
const MAX_WEBHOOKS_PER_WORKSPACE = 3

export function WebhooksContent() {
	const [editingWebhookId, setEditingWebhookId] = useState<string | null>(null)
	const [isCreateMode, setIsCreateMode] = useState(false)

	const {isLoading: isSubscriptionLoading, isWebhooksEnabled} =
		useWebhookSubscription()
	const {data, isLoading: isWebhooksListLoading} =
		webhooksApi.useGetWebhooksQuery()

	const webhooks = data?.documents ?? []
	const editingWebhook = editingWebhookId
		? webhooks.find(w => w.id === editingWebhookId)
		: undefined
	const isEditingWebhookLoading = false

	const [createWebhook, createOptions] = webhooksApi.useCreateWebhookMutation()
	const [updateWebhook, updateOptions] = webhooksApi.useUpdateWebhookMutation()
	const isSaving = createOptions.isLoading || updateOptions.isLoading

	const isWebhookLimitReached = webhooks.length >= MAX_WEBHOOKS_PER_WORKSPACE
	const hasForm = Boolean(isWebhooksEnabled && (isCreateMode || editingWebhookId))
	const isPageReady = !isWebhooksListLoading && !isSubscriptionLoading

	const form = useWebhookForm({
		editingWebhook,
		editingWebhookId,
		isCreateMode,
		hasForm,
		canShowForm: isCreateMode || !isEditingWebhookLoading
	})

	const canShowForm = isCreateMode || (form.formPopulatedForEdit && !isEditingWebhookLoading)

	const handleAddClick = () => {
		if (!isWebhooksEnabled || isWebhookLimitReached) {
			return
		}
		setEditingWebhookId(null)
		setIsCreateMode(true)
		form.resetForm()
	}

	const handleEditClick = (id: string) => {
		if (!isWebhooksEnabled) {
			return
		}
		setIsCreateMode(false)
		setEditingWebhookId(id)
	}

	const handleCancel = () => {
		setEditingWebhookId(null)
		setIsCreateMode(false)
		form.setFormPopulatedForEdit(false)
		form.resetForm()
	}

	const handleSubmit = async (payload: CreateWebhookPayload) => {
		try {
			if (editingWebhookId) {
				await updateWebhook({id: editingWebhookId, data: payload}).unwrap()
			} else {
				await createWebhook(payload).unwrap()
			}
			handleCancel()
		} catch (error) {
			notifyError(error)
		}
	}

	if (!isPageReady) {
		return <FullLoader/>
	}

	return (
		<div className={css.container}>
			<div className={css.aboutSection}>
				<h2 className={css.aboutTitle}>Webhooks</h2>
				<p className={css.aboutText}>
					Use{' '}
					<a
						className={css.aboutLink}
						href={'https://snaily.io/support-articles/support-integrations-webhooks/'}
						rel="noopener noreferrer"
						target="_blank"
					>
						webhooks
					</a>{' '}
					to send notifications to external applications in response to events
					in your Snaily workspace.
				</p>

				{!isWebhooksEnabled && (
					<div className={css.notice}>
						<div className={css.noticeContent}>
							<div>
								<div className={css.noticeText}>
									Webhooks are available in <strong>Pro</strong> and{' '}
									<strong>Team</strong> subscription plans.
								</div>
								<div className={css.noticeSubtext}>
									Consider to upgrade to one of them to utilize power of webhooks.
								</div>
							</div>
						</div>
						<Button
							as={Link}
							to="/upgrade"
							typography={typo.buttonSmB}
							variant="primary"
						>
							Upgrade subscription
						</Button>
					</div>
				)}
			</div>

			<div className={css.webhooksSection}>
				<div className={css.webhooksHeader}>
					<h2 className={css.webhooksTitle}>Webhooks</h2>
					{!hasForm && (
						<Button
							className={css.addButton}
							disabled={!isWebhooksEnabled || isWebhookLimitReached}
							onClick={handleAddClick}
							type="button"
							variant="primary"
						>
							Add new webhook
						</Button>
					)}
				</div>

				{hasForm && !canShowForm && <FullLoader/>}
				{hasForm && canShowForm && (
					<WebhookForm
						form={form}
						isSaving={isSaving}
						isEditing={!!editingWebhookId}
						onSubmit={handleSubmit}
						onCancel={handleCancel}
					/>
				)}

				{!hasForm && (
					<WebhookList
						canManage={isWebhooksEnabled}
						onEdit={handleEditClick}
						webhooks={webhooks}
					/>
				)}
			</div>
		</div>
	)
}